/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { Navigate, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";

function StepCard({ number, title, text, done, buttonText, onClick }) {
  return (
    <div style={styles.stepCard}>
      <div
        style={{
          ...styles.stepNumber,
          ...(done ? styles.stepNumberDone : {}),
        }}
      >
        {done ? "✓" : number}
      </div>

      <div style={styles.stepContent}>
        <div style={styles.stepHeader}>
          <div style={styles.stepTitle}>{title}</div>
          <div
            style={{
              ...styles.stepStatus,
              ...(done ? styles.stepStatusDone : styles.stepStatusPending),
            }}
          >
            {done ? "Done" : "Pending"}
          </div>
        </div>

        <div style={styles.stepText}>{text}</div>

        {!done ? (
          <button style={styles.stepButton} onClick={onClick}>
            {buttonText}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function SchoolAdminOnboarding() {
  const { user, profile, role, loading, profileLoading } = useAuth();
  const navigate = useNavigate();

  const [settingsData, setSettingsData] = useState(null);
  const [progress, setProgress] = useState({
    branding: false,
    classes: false,
    students: false,
    teachers: false,
    parents: false,
  });
  const [saving, setSaving] = useState(false);

  const schoolId = profile?.schoolId || null;
  const effectiveRole = String(role || profile?.role || "").toLowerCase();

  useEffect(() => {
    async function loadProgress() {
      if (!schoolId) return;

      try {
        const settingsRef = doc(db, "schools", schoolId, "settings", "school");
        const settingsSnap = await getDoc(settingsRef);

        const settings = settingsSnap.exists() ? settingsSnap.data() || {} : {};
        setSettingsData(settings);

        setProgress({
          branding: !!(
            (settings.schoolName || settings.name) &&
            settings.primaryColor
          ),
          classes: !!settings.classesReady,
          students: !!settings.studentsReady,
          teachers: !!settings.teachersReady,
          parents: !!settings.parentsReady,
        });
      } catch (err) {
        console.error("Failed to load onboarding progress:", err);
      }
    }

    loadProgress();
  }, [schoolId]);

  const completedCount = useMemo(
    () => Object.values(progress).filter(Boolean).length,
    [progress]
  );

  const canFinish = useMemo(
    () =>
      progress.branding &&
      progress.classes &&
      progress.students &&
      progress.teachers &&
      progress.parents,
    [progress]
  );

  if (loading || profileLoading) {
    return <div style={styles.loading}>Preparing school onboarding...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (effectiveRole !== "admin") {
    return <Navigate to="/" replace />;
  }

  if (profile?.onboardingCompleted) {
    return <Navigate to="/admin-manager" replace />;
  }

  async function handleCompleteSetup() {
    if (!schoolId || !user?.uid) return;

    try {
      setSaving(true);

      await updateDoc(doc(db, "users", user.uid), {
        onboardingCompleted: true,
        onboardingRequired: false,
        updatedAt: serverTimestamp(),
      });

      await updateDoc(doc(db, "schools", schoolId, "settings", "school"), {
        onboardingCompleted: true,
        onboardingStep: 999,
        updatedAt: serverTimestamp(),
      });

      navigate("/admin-manager", { replace: true });
    } catch (err) {
      console.error("Failed to complete onboarding:", err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.hero}>
        <div>
          <div style={styles.eyebrow}>School admin first run</div>
          <h1 style={styles.title}>Complete your school setup</h1>
          <p style={styles.subtitle}>
            Upload your school branding, build your classes, add students,
            invite teachers, and invite parents. After that, your full admin
            workspace takes over.
          </p>
        </div>

        <div style={styles.heroCard}>
          <div style={styles.heroCardLabel}>Setup progress</div>
          <div style={styles.heroCardValue}>{completedCount}/5</div>
          <div style={styles.heroCardSub}>
            {settingsData?.schoolName || settingsData?.name || "Your school"} is
            almost ready
          </div>
        </div>
      </div>

      <div style={styles.stepsGrid}>
        <StepCard
          number="1"
          title="Upload school branding"
          text="Add the official school name, logo, and brand colour in Settings."
          done={progress.branding}
          buttonText="Open Settings"
          onClick={() => navigate("/admin-manager?tab=settings")}
        />

        <StepCard
          number="2"
          title="Create classes"
          text="Set up all classes first so teachers and students can be assigned properly."
          done={progress.classes}
          buttonText="Open Classes"
          onClick={() => navigate("/admin-manager?tab=classes")}
        />

        <StepCard
          number="3"
          title="Add students"
          text="Create student records manually now. CSV upload can be added next."
          done={progress.students}
          buttonText="Open Students"
          onClick={() => navigate("/admin-manager?tab=students")}
        />

        <StepCard
          number="4"
          title="Invite teachers"
          text="Add your teachers and assign them to the correct classes."
          done={progress.teachers}
          buttonText="Open Teachers"
          onClick={() => navigate("/admin-manager?tab=teachers")}
        />

        <StepCard
          number="5"
          title="Invite parents"
          text="Create parent access and link them to the correct children."
          done={progress.parents}
          buttonText="Open Parents"
          onClick={() => navigate("/admin-manager?tab=parents")}
        />
      </div>

      <div style={styles.finishCard}>
        <div>
          <div style={styles.finishTitle}>Finish onboarding</div>
          <div style={styles.finishText}>
            When all setup steps are complete, unlock the normal school admin
            dashboard.
          </div>
        </div>

        <button
          style={{
            ...styles.finishButton,
            ...(canFinish ? {} : styles.finishButtonDisabled),
          }}
          disabled={!canFinish || saving}
          onClick={handleCompleteSetup}
        >
          {saving ? "Finishing..." : "Complete Setup"}
        </button>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f4f7fb",
    padding: 24,
    boxSizing: "border-box",
    display: "grid",
    gap: 18,
  },
  loading: {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    background: "#f4f7fb",
    color: "#112042",
    fontWeight: 900,
    fontSize: 18,
  },
  hero: {
    display: "grid",
    gridTemplateColumns: "1.15fr 0.85fr",
    gap: 16,
    borderRadius: 30,
    background: "linear-gradient(180deg,#ffffff 0%,#f8fbff 100%)",
    border: "1px solid #dbe7ff",
    padding: 24,
  },
  eyebrow: {
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.13em",
    fontWeight: 900,
    color: "#4F46E5",
    marginBottom: 10,
  },
  title: {
    margin: 0,
    fontSize: 44,
    lineHeight: 0.95,
    letterSpacing: "-0.05em",
    color: "#112042",
    fontWeight: 900,
  },
  subtitle: {
    marginTop: 14,
    marginBottom: 0,
    color: "#64748b",
    fontSize: 15,
    lineHeight: 1.75,
    maxWidth: 700,
  },
  heroCard: {
    borderRadius: 24,
    padding: 22,
    color: "#fff",
    background: "linear-gradient(135deg,#4f46e5 0%,#6d5cff 100%)",
    boxShadow: "0 16px 32px rgba(79,70,229,0.16)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  heroCardLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    fontWeight: 900,
    color: "rgba(255,255,255,0.80)",
    marginBottom: 10,
  },
  heroCardValue: {
    fontSize: 42,
    lineHeight: 1,
    fontWeight: 900,
    marginBottom: 10,
  },
  heroCardSub: {
    fontSize: 14,
    color: "rgba(255,255,255,0.92)",
    lineHeight: 1.6,
  },
  stepsGrid: {
    display: "grid",
    gap: 14,
  },
  stepCard: {
    display: "grid",
    gridTemplateColumns: "56px 1fr",
    gap: 14,
    borderRadius: 22,
    background: "#fff",
    border: "1px solid #dbe7ff",
    padding: 16,
  },
  stepNumber: {
    width: 42,
    height: 42,
    borderRadius: 14,
    display: "grid",
    placeItems: "center",
    background: "#eef2ff",
    color: "#4338ca",
    fontWeight: 900,
  },
  stepNumberDone: {
    background: "#dcfce7",
    color: "#166534",
  },
  stepContent: {
    minWidth: 0,
  },
  stepHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
    marginBottom: 8,
  },
  stepTitle: {
    fontSize: 20,
    fontWeight: 900,
    color: "#112042",
  },
  stepStatus: {
    borderRadius: 999,
    padding: "6px 10px",
    fontSize: 11,
    fontWeight: 900,
    whiteSpace: "nowrap",
  },
  stepStatusDone: {
    background: "#dcfce7",
    color: "#166534",
    border: "1px solid #bbf7d0",
  },
  stepStatusPending: {
    background: "#eef2ff",
    color: "#4338ca",
    border: "1px solid #c7d2fe",
  },
  stepText: {
    color: "#64748b",
    fontSize: 14,
    lineHeight: 1.7,
  },
  stepButton: {
    marginTop: 12,
    border: "none",
    borderRadius: 14,
    padding: "11px 14px",
    background: "linear-gradient(135deg,#4f46e5 0%,#6d5cff 100%)",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
  },
  finishCard: {
    display: "flex",
    justifyContent: "space-between",
    gap: 18,
    alignItems: "center",
    borderRadius: 24,
    background: "#fff",
    border: "1px solid #dbe7ff",
    padding: 20,
  },
  finishTitle: {
    fontSize: 22,
    fontWeight: 900,
    color: "#112042",
    marginBottom: 6,
  },
  finishText: {
    color: "#64748b",
    lineHeight: 1.7,
    fontSize: 14,
  },
  finishButton: {
    border: "none",
    borderRadius: 16,
    padding: "14px 18px",
    minWidth: 180,
    background: "linear-gradient(135deg,#4f46e5 0%,#6d5cff 100%)",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
  },
  finishButtonDisabled: {
    opacity: 0.45,
    cursor: "not-allowed",
  },
};